import { create } from '@fhs/zustand';
import { CartPaymentCardType } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { IUserAccountsQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';

import { CardData, PaymentMethodType } from '../../payment';
import { IEncryptionService, ISavedCardInfo } from '../../payment/payment-types';
import {
  detectCardBrand,
  mapCardBrandToCartPaymentCardType,
} from '../../payment/services/detect-card-brand-utils';

export interface SetUserAccountsDataOptions {
  canUseApplePay?: boolean;
  canUseGooglePay?: boolean;
}

export interface PaymentMethodStore {
  showAddPaymentMethod: boolean;
  newCardData: CardData | null;
  creditType: CartPaymentCardType | null;
  selectedPaymentMethod: PaymentMethodType;
  /** When selectedPaymentMethod is 'giftCard', which account to use (for multiple gift cards) */
  selectedGiftCardAccountId: string | undefined;
  /** Used to keep newly vaulted card selected until user.details.defaultAccountIdentifier catches up */
  optimisticDefaultAccountId: string | undefined;
  /** When true, setUserAccountsData will skip auto-selection and preserve the current selectedPaymentMethod */
  preservePaymentMethodSelection: boolean;
  userAccountsData: IUserAccountsQuery;
  savedCardData: ISavedCardInfo | undefined;
  encryptionService: IEncryptionService | null;
  failedVaulting: boolean;
  setSelectedPaymentMethod: (method: PaymentMethodType | null) => void;
  setSelectedGiftCardAccountId: (accountId: string | undefined) => void;
  /** Prefer this account as default on next setUserAccountsData (e.g. after vaulting a new card). Cleared after use. */
  setOptimisticDefaultAccountId: (accountId: string | undefined) => void;
  /** Set to true before manually selecting a payment method to prevent auto-selection from overriding it */
  setPreservePaymentMethodSelection: (preserve: boolean) => void;
  setShowAddPaymentMethod: (show: boolean) => void;
  setUserAccountsData: (
    res: IUserAccountsQuery,
    defaultAccountIdentifier?: string,
    options?: SetUserAccountsDataOptions
  ) => void;
  setSelectedSavedCard: (cardInfo: ISavedCardInfo) => void;
  setNewCardData: (data: CardData | null) => void;
  setCreditType: (cardNumber: string | CartPaymentCardType) => void;
  setEncryptionService: (service: IEncryptionService | null) => void;
  setFailedVaulting: (isFailed: boolean) => void;
  isPaymentReady: () => boolean;
  removeNewCardData: () => void;
  resetPaymentMethodStore: () => void;
}

const initialState = {
  showAddPaymentMethod: false,
  selectedPaymentMethod: null,
  selectedGiftCardAccountId: undefined,
  optimisticDefaultAccountId: undefined,
  preservePaymentMethodSelection: false,
  userAccountsData: {},
  newCardData: null,
  savedCardData: undefined,
  encryptionService: null,
  failedVaulting: false,
  creditType: null,
};

function resolveDefaultPaymentMethod(
  res: IUserAccountsQuery,
  defaultAccountIdentifier: string | undefined,
  options?: SetUserAccountsDataOptions
): {
  selectedPaymentMethod: PaymentMethodType;
  savedCardData: ISavedCardInfo | undefined;
  creditType: CartPaymentCardType | null;
  showAddPaymentMethod: boolean;
  selectedGiftCardAccountId: string | undefined;
} {
  const accounts = res?.userAccounts?.accounts?.filter(Boolean) ?? [];
  const canUseGooglePay = options?.canUseGooglePay ?? false;
  const canUseApplePay = options?.canUseApplePay ?? false;

  // 1) Default is Google Pay or Apple Pay (identifiers not in accounts array)
  if (defaultAccountIdentifier === 'GOOGLE_PAY' && canUseGooglePay) {
    return {
      selectedPaymentMethod: 'googlePay',
      savedCardData: undefined,
      creditType: null,
      showAddPaymentMethod: false,
      selectedGiftCardAccountId: undefined,
    };
  }
  if (defaultAccountIdentifier === 'APPLE_PAY' && canUseApplePay) {
    return {
      selectedPaymentMethod: 'applePay',
      savedCardData: undefined,
      creditType: null,
      showAddPaymentMethod: false,
      selectedGiftCardAccountId: undefined,
    };
  }

  // 2) Default matches a saved account (credit card or gift card are mutually exclusive per account)
  const matchingAccount = defaultAccountIdentifier
    ? accounts.find(
        (acc: any) =>
          acc?.accountIdentifier === defaultAccountIdentifier ||
          acc?.fdAccountId === defaultAccountIdentifier
      )
    : undefined;

  if (matchingAccount) {
    if (matchingAccount.credit?.__typename === 'CreditCard') {
      const savedCardData = {
        ...matchingAccount.credit,
        __typename: 'CreditCard' as const,
        accountIdentifier: matchingAccount.accountIdentifier ?? '',
      } as ISavedCardInfo;
      return {
        selectedPaymentMethod: 'savedCard',
        savedCardData,
        creditType: mapCardBrandToCartPaymentCardType(savedCardData.cardType),
        showAddPaymentMethod: false,
        selectedGiftCardAccountId: undefined,
      };
    }
    if (matchingAccount.giftCard) {
      const accountId = matchingAccount.accountIdentifier ?? '';
      return {
        selectedPaymentMethod: 'giftCard',
        savedCardData: undefined,
        creditType: null,
        showAddPaymentMethod: false,
        selectedGiftCardAccountId: accountId || undefined,
      };
    }
  }

  // 3) No matching default: prefer Google Pay, then Apple Pay, else add new card
  if (canUseGooglePay) {
    return {
      selectedPaymentMethod: 'googlePay',
      savedCardData: undefined,
      creditType: null,
      showAddPaymentMethod: false,
      selectedGiftCardAccountId: undefined,
    };
  }
  if (canUseApplePay) {
    return {
      selectedPaymentMethod: 'applePay',
      savedCardData: undefined,
      creditType: null,
      showAddPaymentMethod: false,
      selectedGiftCardAccountId: undefined,
    };
  }

  return {
    selectedPaymentMethod: null,
    savedCardData: undefined,
    creditType: null,
    showAddPaymentMethod: true,
    selectedGiftCardAccountId: undefined,
  };
}

export const usePaymentMethodStore = create<PaymentMethodStore>((set, get) => ({
  ...initialState,
  setSelectedPaymentMethod: id => set({ selectedPaymentMethod: id }),
  setSelectedGiftCardAccountId: accountId =>
    set({ selectedGiftCardAccountId: accountId, selectedPaymentMethod: 'giftCard' }),
  setShowAddPaymentMethod: show => set({ showAddPaymentMethod: show }),
  setOptimisticDefaultAccountId: accountId => set({ optimisticDefaultAccountId: accountId }),
  setPreservePaymentMethodSelection: preserve => set({ preservePaymentMethodSelection: preserve }),
  setUserAccountsData: (res, defaultAccountIdentifier, options) => {
    const { preservePaymentMethodSelection } = get();

    // If preservePaymentMethodSelection is true, skip auto-selection and just update the accounts data
    if (preservePaymentMethodSelection) {
      set({
        userAccountsData: res,
        preservePaymentMethodSelection: false, // Clear the flag after use
      });
      return;
    }

    // Prefer optimistic default (e.g. newly vaulted card) so UI doesn't revert before user.details updates
    const optimistic = get().optimisticDefaultAccountId;
    const effectiveDefault = optimistic ?? defaultAccountIdentifier;
    const resolved = resolveDefaultPaymentMethod(res, effectiveDefault, options);
    // Clear optimistic only when profile has caught up, so a second setUserAccountsData doesn't revert
    const clearOptimistic = optimistic != null && defaultAccountIdentifier === optimistic;
    set({
      userAccountsData: res,
      ...resolved,
      optimisticDefaultAccountId: clearOptimistic ? undefined : optimistic,
    });
  },
  setSelectedSavedCard: cardInfo => {
    const creditType = mapCardBrandToCartPaymentCardType(cardInfo.cardType);
    set({
      savedCardData: cardInfo,
      creditType,
      selectedPaymentMethod: 'savedCard',
      showAddPaymentMethod: false,
      selectedGiftCardAccountId: undefined,
    });
  },
  setNewCardData: data => set({ newCardData: data }),
  setCreditType: cardNumberOrType => {
    if (typeof cardNumberOrType === 'string') {
      set({ creditType: detectCardBrand(cardNumberOrType) });
    } else {
      set({ creditType: cardNumberOrType });
    }
  },
  setEncryptionService: service => set({ encryptionService: service }),
  removeNewCardData: () => set({ newCardData: null }),
  resetPaymentMethodStore: () =>
    set({
      ...initialState,
    }),
  setFailedVaulting: (isFailed: boolean) => set({ failedVaulting: isFailed }),
  isPaymentReady: (): boolean => {
    const { selectedPaymentMethod, newCardData } = get();

    switch (selectedPaymentMethod) {
      case null:
        return !!(
          newCardData?.cardNumber &&
          newCardData?.cvv &&
          newCardData?.expiryMonth &&
          newCardData?.expiryYear &&
          newCardData?.postalCode
        );

      case 'savedCard':
      case 'applePay':
      case 'googlePay':
      case 'giftCard':
        return true;

      default:
        return false;
    }
  },
}));
